// Vertical timeline component for displaying day schedule
// REFACTORED: Extracted common logic into reusable components and hooks
// UPDATED: Uses TimelineContext to eliminate prop drilling
// - TimelineColumn: 24 props -> 5 props
// - TimelineTaskBlock: 21 props -> 5 props
// - DayTimeline wraps children with TimelineProvider
// OPTIMIZED: Mobile UX with natural page scroll (no internal overflow container)

import { useMemo, useRef, useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { getFullName } from '../../lib/supabase';
import { getShortName } from '../../lib/nameUtils';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useDragDropTimeline, type TimelineTask } from '../../hooks/useDragDropTimeline';
import { useSmartTimelineScroll } from '../../hooks/useSmartTimelineScroll';
import { TimelineColumn, TIMELINE_HEIGHT } from './timeline';
import { TimelineTaskBlock } from './timeline/TimelineTaskBlock';
import { TimelineGrid } from './timeline/TimelineGrid';
import { CurrentTimeIndicator } from './timeline/CurrentTimeIndicator';
import { TimelineProvider, type TimelineContextValue } from './timeline/TimelineContext';
import { timeToMinutes, doTimeSlotsOverlap } from '../../lib/timeCalculations';
import { Z_STICKY_HEADER, Z_TOOLTIP } from '../../lib/zIndex';

interface Operator {
  id: string;
  first_name: string;
  last_name: string;
  username: string;
}

interface DayTimelineProps {
  tasks: TimelineTask[];
  date: Date;
  operatorIds?: string[];
  operators?: Operator[];
  onTaskClick?: (task: TimelineTask) => void;
  onTaskDrop?: (taskId: string, fromOperatorId: string, toOperatorId: string, task: TimelineTask, newScheduledTime: string) => void;
  rollbackRef?: React.MutableRefObject<(() => void) | null>;
  scrollTriggerKey?: number;
  conflictingTaskIds?: Set<string>;
}

interface TaskLayout {
  task: TimelineTask;
  column: number;
  totalColumns: number;
}

function calculateTaskLayouts(
  tasks: TimelineTask[],
  overlap: (t1: TimelineTask, t2: TimelineTask) => boolean
): TaskLayout[] {
  const sorted = [...tasks].sort((a, b) => {
    const startDiff = timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
    if (startDiff !== 0) return startDiff;
    return b.duration - a.duration;
  });

  const layouts: TaskLayout[] = [];

  sorted.forEach((task, index) => {
    const overlappingTasks = sorted.filter((otherTask, otherIndex) => {
      if (otherIndex === index) return false;
      return overlap(task, otherTask);
    });

    if (overlappingTasks.length === 0) {
      layouts.push({ task, column: 0, totalColumns: 1 });
    } else {
      const assignedOverlapping = layouts.filter(layout =>
        overlappingTasks.some(ot => ot.id === layout.task.id)
      );

      const usedColumns = new Set(assignedOverlapping.map(l => l.column));

      let column = 0;
      while (usedColumns.has(column)) {
        column++;
      }

      const maxColumn = Math.max(column, ...assignedOverlapping.map(l => l.column));
      const totalColumns = maxColumn + 1;

      assignedOverlapping.forEach(layout => {
        layout.totalColumns = Math.max(layout.totalColumns, totalColumns);
      });

      layouts.push({ task, column, totalColumns });
    }
  });

  return layouts;
}

function tasksOverlap(task1: TimelineTask, task2: TimelineTask): boolean {
  return doTimeSlotsOverlap(
    { startTime: task1.startTime, duration: task1.duration },
    { startTime: task2.startTime, duration: task2.duration }
  );
}

export { type TimelineTask };

export function DayTimeline({
  tasks,
  date,
  operatorIds = [],
  operators = [],
  onTaskClick,
  onTaskDrop,
  rollbackRef,
  scrollTriggerKey,
  conflictingTaskIds = new Set()
}: DayTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const singleTimelineRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const { isMobile } = useResponsiveLayout();

  const {
    draggedTask,
    draggedTaskPosition,
    isDragRollback,
    isTouchDragging,
    scrollContainerRef,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
    canReassignTask
  } = useDragDropTimeline({
    tasks,
    onTaskDrop,
    rollbackRef
  });

  const timelineContextValue: TimelineContextValue = {
    draggedTask,
    draggedTaskPosition,
    isDragRollback,
    isTouchDragging,
    conflictingTaskIds,
    scrollContainerRef,
    isMobile,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchCancel,
    onTaskClick,
    canReassignTask,
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    setContainerWidth(container.offsetWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useSmartTimelineScroll({
    tasks,
    date,
    scrollContainerRef,
    scrollTriggerKey,
    isMobile
  });

  const isToday = useMemo(() => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }, [date]);

  const tasksWithLayout = useMemo(() => {
    return calculateTaskLayouts(tasks, tasksOverlap);
  }, [tasks]);

  const isMultiOperator = operatorIds && operatorIds.length > 1;

  if (tasks.length === 0 && !isMultiOperator) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12">
        <div className="text-center">
          <Clock className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">Aucune mission planifiee pour cette date</p>
        </div>
      </div>
    );
  }

  if (isMultiOperator) {
    return (
      <TimelineProvider value={timelineContextValue}>
        <MultiOperatorTimeline
          containerRef={containerRef}
          scrollContainerRef={scrollContainerRef}
          containerWidth={containerWidth}
          operatorIds={operatorIds}
          operators={operators}
          tasks={tasks}
          isToday={isToday}
          isMobile={isMobile}
          draggedTaskPosition={draggedTaskPosition}
        />
      </TimelineProvider>
    );
  }

  return (
    <TimelineProvider value={timelineContextValue}>
      <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col ${isMobile ? '' : 'h-full'}`}>
        <div
          ref={scrollContainerRef}
          className={`flex-1 p-4 ${isMobile ? 'pb-8' : 'overflow-y-auto min-h-0'}`}
        >
          <div className="flex gap-4">
            <div className={`flex-shrink-0 ${isMobile ? 'w-12' : 'w-16'}`}>
              <div className="relative" style={{ height: `${TIMELINE_HEIGHT}px` }}>
                {Array.from({ length: 24 }, (_, i) => (
                  <div
                    key={i}
                    className="absolute left-0 right-0 flex items-center"
                    style={{ top: `${i * 60}px`, transform: 'translateY(-50%)' }}
                  >
                    <span className={`font-medium text-gray-600 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                      {isMobile ? `${i}h` : `${i.toString().padStart(2, '0')}:00`}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div
              className={`flex-1 relative transition-colors ${
                draggedTaskPosition ? 'bg-blue-50/30' : ''
              }`}
              onDragOver={(e) => handleDragOver(e, operatorIds[0] || '', singleTimelineRef.current)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, operatorIds[0] || '')}
            >
              <div ref={singleTimelineRef} data-timeline-column className="relative" style={{ height: `${TIMELINE_HEIGHT}px` }}>
                <TimelineGrid lineColor="bg-gray-300" />

                {isToday && <CurrentTimeIndicator showLabel={!isMobile} />}

                <div className="absolute inset-0">
                  {tasksWithLayout.map(({ task, column, totalColumns }) => (
                    <TimelineTaskBlock
                      key={task.id}
                      task={task}
                      column={column}
                      totalColumns={totalColumns}
                      operatorId={operatorIds[0] || ''}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TimelineProvider>
  );
}

interface MultiOperatorTimelineProps {
  containerRef: React.RefObject<HTMLDivElement>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  containerWidth: number;
  operatorIds: string[];
  operators: Operator[];
  tasks: TimelineTask[];
  isToday: boolean;
  isMobile: boolean;
  draggedTaskPosition: { time: string; operatorId: string } | null;
}

function MultiOperatorTimeline({
  containerRef,
  scrollContainerRef,
  containerWidth,
  operatorIds,
  operators,
  tasks,
  isToday,
  isMobile,
  draggedTaskPosition
}: MultiOperatorTimelineProps) {
  const operatorData = operatorIds.map(opId => {
    const operatorTasks = tasks.filter(t => t.operatorId === opId);
    const operator = operators.find(op => op.id === opId);
    const operatorName = operator
      ? getShortName(operator)
      : (operatorTasks[0]?.operatorName || 'Operateur');
    const operatorFullName = operator ? getFullName(operator) : operatorName;
    return { opId, operatorName, operatorFullName, operatorTasks };
  });

  const MIN_COLUMN_WIDTH = isMobile ? 140 : 100;
  const timeColumnWidth = isMobile ? 56 : 72;

  const calculateColumnWidth = (): number => {
    if (containerWidth === 0 || operatorData.length === 0) return 240;
    const availableWidth = containerWidth - timeColumnWidth;
    const idealWidth = availableWidth / operatorData.length;
    return idealWidth < MIN_COLUMN_WIDTH ? MIN_COLUMN_WIDTH : idealWidth;
  };

  const columnWidth = calculateColumnWidth();
  const totalContentWidth = operatorData.length * columnWidth;
  const totalWidth = timeColumnWidth + totalContentWidth;

  return (
    <div ref={containerRef} className={`bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col ${isMobile ? '' : 'h-full'}`}>
      <div
        ref={scrollContainerRef}
        className={`flex-1 ${isMobile ? 'pb-8' : 'overflow-auto min-h-0'}`}
      >
        <div className="relative">
          <div className="sticky top-0 flex bg-gray-50 border-b-2 border-gray-300" style={{ minWidth: `${totalWidth}px`, zIndex: Z_STICKY_HEADER }}>
            <div
              className="sticky left-0 bg-gray-50 border-r-2 border-gray-300 flex-shrink-0"
              style={{ width: `${timeColumnWidth}px`, zIndex: Z_STICKY_HEADER + 1 }}
            />

            <div className="flex bg-gray-50" style={{ minWidth: `${totalContentWidth}px` }}>
              {operatorData.map(({ opId, operatorName, operatorFullName }) => (
                <div
                  key={`header-${opId}`}
                  className="flex-shrink-0 py-3 px-4 border-r border-gray-200"
                  style={{ width: `${columnWidth}px` }}
                  title={operatorFullName}
                >
                  <div className="group relative w-full">
                    <div className="overflow-hidden">
                      <p className="text-sm font-semibold text-gray-900 text-center truncate w-full">
                        {operatorName}
                      </p>
                    </div>
                    {!isMobile && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" style={{ zIndex: Z_TOOLTIP }}>
                        {operatorFullName}
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex" style={{ minWidth: `${totalWidth}px` }}>
            <div
              className="sticky left-0 flex-shrink-0 bg-gray-50 border-r-2 border-gray-300"
              style={{ width: `${timeColumnWidth}px`, zIndex: Z_STICKY_HEADER - 10 }}
            >
              <div className="relative" style={{ height: `${TIMELINE_HEIGHT}px` }}>
                {Array.from({ length: 24 }, (_, i) => (
                  <div
                    key={i}
                    className="absolute left-0 right-0 flex items-center justify-center"
                    style={{ top: `${i * 60 - 10}px` }}
                  >
                    <span className={`font-medium text-gray-700 bg-gray-50 px-2 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                      {isMobile ? `${i}h` : `${i.toString().padStart(2, '0')}:00`}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative" style={{ minWidth: `${totalContentWidth}px`, height: `${TIMELINE_HEIGHT}px` }}>
              {isToday && <CurrentTimeIndicator showLabel={!isMobile} />}

              <div className="flex h-full" style={{ minWidth: `${totalContentWidth}px` }}>
                {operatorData.map(({ opId, operatorTasks }) => (
                  <TimelineColumn
                    key={opId}
                    operatorId={opId}
                    tasks={operatorTasks}
                    columnWidth={columnWidth}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
